import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getCallLogs } from '../../api/callLogs';


const formatDuration = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}분 ${s}초` : `${s}초`;
};

const formatDateTime = (str) => {
    if (!str) return '-';
    return new Date(str).toLocaleString('ko-KR', {
        month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
};

const STATUS_LABEL = {
    COMPLETED: '완료',
    MISSED: '부재',
    FAILED: '실패',
};

const STATUS_COLOR = {
    COMPLETED: '#22c55e',
    MISSED: '#f59e0b',
    FAILED: '#EF4444',
};


const CallLogsPage = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [목록, set목록] = useState([]);
    const [로딩중, set로딩중] = useState(true);
    const [에러, set에러] = useState(null);
    const [선택로그, set선택로그] = useState(null);

    useEffect(() => {
        getCallLogs(uuid)
            .then((data) => {
                set목록(Array.isArray(data?.call_logs) ? data.call_logs : []);
            })
            .catch(() => set에러('통화 이력을 불러오지 못했습니다.'))
            .finally(() => set로딩중(false));
    }, [uuid]);

    const parseMessages = (raw) => {
        try { return JSON.parse(raw); } catch { return []; }
    };

    return (
        <PageWrapper>
            <Contents>
                <BackButton onClick={() => navigate('/stores')}>
                    ← 스토어 목록
                </BackButton>

                <PageTitle>통화 이력</PageTitle>

                {로딩중 && <StatusText>불러오는 중...</StatusText>}
                {!로딩중 && 에러 && <StatusText $error>{에러}</StatusText>}
                {!로딩중 && !에러 && 목록.length === 0 && (
                    <StatusText>아직 통화 이력이 없습니다.</StatusText>
                )}

                {!로딩중 && !에러 && 목록.length > 0 && (
                    <Layout>
                        <LogList>
                            {목록.map((log) => (
                                <LogCard
                                    key={log.uuid}
                                    $active={선택로그?.uuid === log.uuid}
                                    onClick={() => set선택로그(log)}
                                >
                                    <LogCardTop>
                                        <StatusBadge $color={STATUS_COLOR[log.status] ?? '#aaaaaa'}>
                                            {STATUS_LABEL[log.status] ?? log.status}
                                        </StatusBadge>
                                        <LogTime>{formatDateTime(log.started_at)}</LogTime>
                                    </LogCardTop>

                                    {log.summary && (
                                        <LogSummary>{log.summary}</LogSummary>
                                    )}

                                    <LogMeta>
                                        <MetaItem>
                                            <MetaLabel>발신</MetaLabel>
                                            <MetaValue>{log.caller_number}</MetaValue>
                                        </MetaItem>
                                        <MetaItem>
                                            <MetaLabel>수신</MetaLabel>
                                            <MetaValue>{log.callee_number}</MetaValue>
                                        </MetaItem>
                                        {log.duration_sec != null && (
                                            <MetaItem>
                                                <MetaLabel>통화</MetaLabel>
                                                <MetaValue>{formatDuration(log.duration_sec)}</MetaValue>
                                            </MetaItem>
                                        )}
                                    </LogMeta>
                                </LogCard>
                            ))}
                        </LogList>

                        {선택로그 && (
                            <DetailPanel>
                                <DetailHeader>
                                    <DetailTitle>통화 대화 내용</DetailTitle>
                                    <CloseDetail onClick={() => set선택로그(null)}>✕</CloseDetail>
                                </DetailHeader>

                                {선택로그.summary && (
                                    <SummaryBox>
                                        <SummaryLabel>AI 요약</SummaryLabel>
                                        <SummaryText>{선택로그.summary}</SummaryText>
                                    </SummaryBox>
                                )}

                                <MessageList>
                                    {parseMessages(선택로그.messages).map((msg, i) => (
                                        <MessageBubble key={i} $role={msg.role}>
                                            <BubbleLabel>{msg.role === 'assistant' ? '띠링' : '고객'}</BubbleLabel>
                                            <BubbleText>{msg.content}</BubbleText>
                                        </MessageBubble>
                                    ))}
                                </MessageList>
                            </DetailPanel>
                        )}
                    </Layout>
                )}
            </Contents>
        </PageWrapper>
    );
};


const PageWrapper = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
`;

const Contents = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 80px 16px 48px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #999999;
    cursor: pointer;
    align-self: flex-start;

    &:hover { color: #111111; }
`;

const PageTitle = styled.h1`
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const StatusText = styled.span`
    display: block;
    font-family: Pretendard;
    font-size: 15px;
    color: ${({ $error }) => ($error ? '#EF4444' : '#aaaaaa')};
`;

const Layout = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
`;

const LogList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 0 0 380px;
    min-width: 0;
`;

const LogCard = styled.div`
    background: #ffffff;
    border: 1px solid ${({ $active }) => ($active ? '#487BFD' : '#eeeeee')};
    border-radius: 12px;
    padding: 18px 20px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
        border-color: ${({ $active }) => ($active ? '#487BFD' : '#bbbbbb')};
        background: ${({ $active }) => ($active ? '#f5f8ff' : '#fafafa')};
    }

    ${({ $active }) => $active && 'background: #f5f8ff;'}
`;

const LogCardTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const StatusBadge = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: ${({ $color }) => $color};
    background: ${({ $color }) => `${$color}18`};
    border: 1px solid ${({ $color }) => `${$color}44`};
    border-radius: 4px;
    padding: 2px 8px;
`;

const LogTime = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    color: #aaaaaa;
`;

const LogSummary = styled.p`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #333333;
    margin: 0;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const LogMeta = styled.div`
    display: flex;
    gap: 16px;
`;

const MetaItem = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const MetaLabel = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 500;
    color: #aaaaaa;
`;

const MetaValue = styled.span`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #555555;
`;

const DetailPanel = styled.div`
    flex: 1;
    min-width: 0;
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 108px;
    max-height: calc(100dvh - 128px);
    overflow-y: auto;
`;

const DetailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const DetailTitle = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #111111;
`;

const CloseDetail = styled.button`
    background: none;
    border: none;
    font-size: 14px;
    color: #aaaaaa;
    cursor: pointer;
    padding: 4px;
    line-height: 1;

    &:hover { color: #333333; }
`;

const SummaryBox = styled.div`
    background: #f0f2ff;
    border: 1px solid #d0d8ff;
    border-radius: 8px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SummaryLabel = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: #487BFD;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

const SummaryText = styled.p`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #333333;
    margin: 0;
    line-height: 1.6;
`;

const MessageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MessageBubble = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: ${({ $role }) => ($role === 'assistant' ? 'flex-start' : 'flex-end')};
`;

const BubbleLabel = styled.span`
    font-family: Pretendard;
    font-size: 10px;
    font-weight: 500;
    color: #aaaaaa;
    padding: 0 4px;
`;

const BubbleText = styled.div`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #111111;
    background: ${({ theme }) => '#f7f8fa'};
    border: 1px solid #eeeeee;
    border-radius: 10px;
    padding: 10px 14px;
    line-height: 1.6;
    max-width: 85%;
    white-space: pre-wrap;
`;


export default CallLogsPage;
